import {
	FileIcon,
	type IconSize,
} from '@bamboohr/fabric';
import { createRoot, type Root } from 'react-dom/client';
import {
	render,
} from 'base/wrapped-render-v2';

/** The attribute name for the esig attribute */
const ATTRIBUTE_ESIG = 'esig';
/** The attribute name for the name attribute */
const ATTRIBUTE_NAME = 'name';
/** The attribute name for the size attribute */
const ATTRIBUTE_SIZE = 'size';

/**
 * The valid sizes for the file icon from Fabric
 */
const fileIconSizes: Array<IconSize> = ['teenie', 'small', 'medium', 'large'];

// Use WeakMap to automatically clean up roots when elements are garbage collected
const rootMap = new WeakMap<HTMLElement, Root>();

export class BaFileIcon extends HTMLElement {
	/**
	 * The element that the custom element is attached to
	 */
	_element: HTMLElement;

	/**
	 * Constructor
	 */
	constructor() {
		super();
		this._element = this;
	}

	/**
	 * Returns the attributes that should be observed for changes
	 */
	static get observedAttributes() {
		return [ATTRIBUTE_ESIG, ATTRIBUTE_NAME, ATTRIBUTE_SIZE];
	}

	/**
	 * Lifecycle method called when the element is added to the DOM
	 */
	connectedCallback() {
		this._injectFileIcon();
	}

	/**
	 * Lifecycle method called when an attribute is change in the DOM
	 */
	attributeChangedCallback() {
		this._injectFileIcon();
	}

	/**
	 * Injects a Fabric FileIcon into the element
	 */
	_injectFileIcon() {
		const esig = this._element.getAttribute(ATTRIBUTE_ESIG) === 'true';
		const name = this._element.getAttribute(ATTRIBUTE_NAME);
		let size = this._element.getAttribute(ATTRIBUTE_SIZE) as IconSize;
		if (!size) {
			size = 'teenie';
		}
		if (!fileIconSizes.includes(size)) {
			size = 'teenie';
		}

		// Get or create root for this element
		let root = rootMap.get(this._element);
		if (!root) {
			root = createRoot(this._element);
			rootMap.set(this._element, root);
		}

		// Render the appropriate component
		if (esig) {
			render({ children: <FileIcon esig={ true } size={ size } type="esig" />, root });
			return;
		}
		render({ children: <FileIcon filename={ name } size={ size } />, root });
	}
}

customElements.define('ba-file-icon', BaFileIcon);
